/*
================================================================================
                    WI ADD/EDIT PAGE - BUSINESS LOGIC
================================================================================

PURPOSE: Welding Instruction slip generation for field welders

KEY FEATURES:
• Project-based WI slip generation with WCS integration
• WPS-WPQ validation and welder qualification verification
• Multi-level linking: Project → Joint → WPS → Process → Welder
• NPCIL project linking with mandatory WPQ validation
• Real-time welder availability based on qualification status
• Automated slip numbering and tracking system

DATA FLOW:
• WCS Project → Joint Numbers → WPS Numbers → Process Types → Qualified Welders
• WPQ linking validation ensures only qualified welders for specific processes
• Last weld date tracking for welder continuity requirements
• Productivity measurement integration for performance tracking

BUSINESS RULES:
• NPCIL projects require mandatory WPQ linking validation
• Welders must have valid qualifications for selected process/position
• Tubesheet welding has special validation and welder selection logic
• Flux details conditionally included based on process requirements
• 24-hour slip validity with automatic status management

INTEGRATION POINTS:
• WCS API: Project and joint validation
• WPS API: Procedure specification retrieval
• WPQ API: Welder qualification verification
• WI API: Slip generation and tracking
• Productivity API: Performance measurement
================================================================================
                    WI ADD/EDIT PAGE - LAST WELD DATE UPDATE LOGIC
================================================================================

PURPOSE: Welding Instruction slip generation with automatic last weld date updates

LAST WELD DATE UPDATE WORKFLOW:
When WI slip is generated (handleAdd function), the system updates last weld dates:

1. RECORDS THAT GET UPDATED:

DETAIL RECORDS (WPQ_Details_Table):
• wld_createdon = Current date (DD-MM-YYYY format)
• WPQ_Welder_ExpiresOn = Current date + 178 days (YYYY-MM-DD format) 
• WI_WPQ_SlipGenerationStatus = "Yes" (marks as used in production)
• Updates ALL detail records with same WPQ_Process_Category and WelderId

HEADER RECORDS (WPQ_Header_Table):
• WQC_Last_Weld_Date array updated with process-wise last weld dates
• Format: [{Process: "GTAW", LastWeldDate: "15-01-2024"}]
• Used for WQC (Welder Qualification Chart) reporting

2. UPDATE TRIGGERS:
• updateDate state: Contains selected WPQ records for Groove/Overlay
• tubeWeldersData state: Contains linked WPQ SK data for Tubesheet
• Both populated by fetchWelder() function during welder selection

3. WPQ TYPE-SPECIFIC LOGIC:

GROOVE/OVERLAY:
• Uses updateDate array (selectedLinkedWeldersData from fetchWelder)
• Calls updatedatewpq() for each qualified WPQ record
• Process category synchronization updates related processes

TUBESHEET:
• Uses tubeWeldersData array (linkingWpqSKData from fetchWelder)
• Special SK format handling with WPSH_WPQ_Type
• Batch updates for linked WPQ records

4. API INTEGRATION:
• updatedatewpq() calls /updatedate endpoint in WPQ Details Router
• Backend calculates current date and 178-day expiry
• Updates both detail and header records for WQC reporting
• Maintains welder qualification validity for continuous production

5. BUSINESS IMPACT:
• Extends welder qualification validity by 178 days from current date
• Ensures continuous production without re-qualification
• Maintains process category synchronization across related qualifications
• Supports WQC reporting with accurate last weld date tracking

================================================================================
*/

import { useState, useEffect } from "react";
import { Col, Form, Modal, Row, Alert } from "react-bootstrap";
import ReactDOM from "react-dom";
import { SearchSelect } from "../utility/forms/inputs/search-select";
import { SearchSelectWithInput } from "../utility/forms/inputs/search-select-with-input";
import SuccessAlertModal from "../Modals/successAlertModal";
import ConfirmAlertModal from "../Modals/confirmAlertModal";
import { useNavigate } from "react-router-dom";
import { isAlphanumericWithSpaceDotDash, twodecimal } from "../../functions/validations";
import { showSuccess, showWarning, showError } from "../utility/ui/notifications/Toaster";
import ButtonModal from "../Modals/buttonModal";
import { getWCSDataHeader } from "../../Services/wcs-services";
import { getWIbyProjectNo } from "../../Services/wps_groove-services";
import { getdetailsByIdWI } from "../../Services/wps_details-service";
import { getbyprojectnoWPQ_WI, getselectedwelder } from "../../Services/wpq_groove-services";
import { addwelder, getCount, getProjectLinkWPQByType, getwpqlink, getallWelderid } from "../../Services/WI-services";
import { getAllOnlyProject } from "../../Services/project-services";
import { updatedatewpq , getdetailsById} from "../../Services/wpq_details-service";
import { getdetailsById as  getWPSDetailsById } from "../../Services/wps_details-service";
import { Plant_Location_WI } from "../utility/common/TransactionComponents/TransactionEntity";
import { General_Single_Input_select } from "../utility/forms/inputs/InputsTransaction";
import { getFcatByFno } from "../../Services/fno-services";
import { getActiveWelder, getWelderById } from "../../Services/Welder-services";
import { getPcatByPno } from "../../Services/pcategory-service";
import { errorstyle, normalstyle } from "../../functions/welding-functions";
import { GridLoader } from "react-spinners";
import { getActiveProcess } from "../../Services/weldingprocess-services";
import { getWPQLinkingData } from "../WPQLinking/WPQLinkingCommon";
import {updateLastWeldDateFromWI} from "../../Services/wpq_details-service";
import WiPdfViewer from "./PDF/WiPdfViewer";
import { getWeldingProcessByProcess } from "../../Services/weldingprocess-services";


/*
 * AddEditWIPage Component - WI slip creation and management
 * 
 * COMPONENT WORKFLOW:
 * 1. Project Selection → WCS validation → Joint number population
 * 2. WPS Selection → Process filtering → Type determination
 * 3. Welder Qualification → WPQ linking validation → Availability check
 * 4. Slip Generation → Continuity tracking → Productivity integration
 * 5. PDF Generation → Print tracking → Status management
 */
const AddEditWIPage = (props) => {
  if (props.rowdata.wld_status === "Active") {
    props.rowdata.wld_status = true;
  } else if (props.rowdata.wld_status === "Inactive") {
    props.rowdata.wld_status = false;
  }

  let action = props.action;
  let formName = "WI Slip";
  const [showSuccessModel, setShowSuccessModel] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showConfirmModel, setshowConfirmModel] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [slipno, getslipno] = useState([]);
  const [UpdatedWps, setgetUpdatedWps] = useState([]);
  const [getheaderData, setgetheaderData] = useState([]);
  const [wcsProjectData, setWcsProjectData] = useState([]);
  const [updatedate, setUpdateDate] = useState([]);
  const [wpsData, setWpsData] = useState([]);
  const [projectDetails, setProjectDetails] = useState([]);

  const [jointNumbers, setJointNumbers] = useState([]);
  const [wpsNumbers, setWpsNumbers] = useState([]);
  const [npcilflag, setNpcilflag] = useState(false);
  const [getWelder, setGetWelder] = useState([]);
  const [fluxDetailsInWISlipCheck, setFluxDetailsInWISlipCheck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeWldProcCategories, setActiveWldProcCategories] = useState([]);
  const [npcilProjDet, setNpcilProjDet] = useState([]);
  const [tubeWeldersData, setTubeWeldersData] = useState([]);
  const [filteredProcesses, setFilteredProcesses] = useState([]);
  const [processTypes, setProcessTypes] = useState([]);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfWpsData, setPdfWpsData] = useState(null);


  let weldingPositions = [
    { "WI_Welding_Position": "F" },
    { "WI_Welding_Position": "H" },
    { "WI_Welding_Position": "V" },
    { "WI_Welding_Position": "O" },
    { "WI_Welding_Position": "SP" },
    { "WI_Welding_Position": "6G" },
    { "WI_Welding_Position": "5G" },
  ];

  const GrooveType = ["Groove", "Tubesheet", "Overlay"];
  const history = useNavigate();
  let username = sessionStorage.getItem("UserName");
  let emailid = sessionStorage.getItem("UserId");
  const [WiObj, setWiObj] = useState(
    action === "Edit"
      ? props.rowdata
      : {
        Processes: [
          {
            SK: "",
            WPSD_WP_FillerMetalProductForm: "",
            WPSH_consumRadio: "",
            WPSD_WP_ArcTransferMode: "",
            WPSD_WP_CurrentPolarity: "",
            WPSD_FM_FNo: "",
            WPSD_WP_Multiple_SinglePassPerSide: "",
            ActiveProcess: "",
            WPQ_WelderId: "",
            WPQ_RecordNo: "",
            WPQ_Link: []
          }
        ],
        PK: "",
        SK: "",
        WI_Slip: "",
        WI_Project_No: "",
        WI_Joint_No: "",
        WI_WPS_No: "",
        WI_Process: "",
        WI_Type: "",
        WI_Overlay_Joint_BMT: "",
        WI_PipePlate: "",
        WI_Pipe_Diameter: "",
        WI_Plate_No: "",
        WI_Welding_Position: "",
        WI_Welder_Id: "",
        WI_Welder_Name: "",
        wld_WPSType: "",
        WPSH_TRQ_BaseMetalCRO_Max: "",
        WPSH_BM_WeldingTo_PNo: "",
        WI_24hrs: "",
        WI_Slip_Status: "",
        WI_print: "",
        WI_reprintDate: "",
        WCS_Project_No: "",
        WI_plant: "",
        WI_FillerMetalSize: "",
        WI_WeldConsumable: "",
        WI_WP_BatchNumber: "",
        WI_CreatedBy: "",
        WI_CreatedBy_Name: "",
        WI_WPSHeaderSK: "",
        WI_WPSDetailSK: "",
        WI_WPQ_PrcessStatus: "",
        WPS_Rev_no: "",
        WI_Flux: "",
        WI_Flux_Brand: "",
        WI_Flux_ItemCode: "",
        WI_Flux_Batch: "",
        WI_FM_Brand: "",
        WI_FM_AWS: "",
        WI_ItemDesc: "",
        WI_WCinKgElectrode: "",
        WI_ProcessCategory: "",
        WI_JointDesign: "",
        WI_Wrap_Insulation: "",
        WI_Weld_Max: "",
        WI_WeldDepThick: "",
        WI_NOTES: "",
      }
  );

  const [error, setError] = useState({
    SK: {},
    PK: {},
    wld_status: {},
    wld_code: {},
    wld_p_category: {},
    wld_description: {},
    wld_createdby: {},
    wld_createdon: {},
    wld_updatedby: {},
    wld_updatedon: {},
    WI_Overlay_Joint_BMT: {},
    WI_plant: {},
    WI_Welder_Id: {},
  });

  const modalOnClick = () => {
    history.push({
      pathname: "/",
      data: "setWiObj",
    });
  };

  const handleCloseSuccess = () => {
    setShowSuccessModel(false);
    props.onHide();
    props.setReload(!props.reload);
  };
  const handleCloseConf = () => {
    setshowConfirmModel(false);
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setWiObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const addUniqueWelders = (sourceArray, uniqueData) => {
    sourceArray.forEach((item) => {
      let wiWldIdArr = item.WI_Welder_Id.split(",");
      wiWldIdArr.forEach(element => {
        if (!uniqueData.some((val) => val.WI_Welder_Id === element)) {
          uniqueData.push({
            WI_Welder_Id: element,
            WI_Welder_Name: "",
          });
        }
      });
    });
    return uniqueData;

  };

  const fetchWelder = async (tempObj) => {
    setLoading(true);
    const uniqueData = [];
    let selectedLinkedWeldersData = []

    const wpqType = wpsData[0]?.wld_WPSType;
    //Step 1 - Check WPS Type
    const isTubesheet = wpqType === "Tubesheet";
    const linkObj = {
      npcilflag,
      projectNumber: WiObj.WI_Project_No,
      jointType: tempObj.wld_WPSType,
      jointNo: WiObj.WI_Joint_No
    };
    //Step 2 - Get linking data -get linking header/parent record
    const linkedproject = await getProjectLinkWPQByType(linkObj);
    console.log("Step 2 linked projects", linkedproject)
    let linkingWpqSKData = [];
    //Step 3 - check if project is linked 
    if (linkedproject?.length > 0 && linkedproject[0]?.WI_Welder_Id !== "") {
      //Step 4 - Get Linked WPQ SK values data (Childs data) -  will be arraye of SK values  like [Welder1#WPQ1, Welder2#WPQ2]
      linkingWpqSKData = await getWPQLinkingData(linkedproject[0]);
      console.log("Step 4 linked projects", linkingWpqSKData)
    }
    let uniqWelderData = [];
    if (isTubesheet) {
      //Step 5 - get unique welders form linked WPQS
      uniqWelderData = addUniqueWelders(linkedproject, uniqueData);
      //Step 5
      setTubeWeldersData(linkingWpqSKData); // for to update last weld date of WPQ
      //setGetWelder(uniqWelderData);
    }
    else {
      //Step 6 - Get welder form WPS and WPQ Comparison - form WI logic
      const selectedWelder = await getselectedwelder(tempObj);
      console.log("Step 6 selectedWelder.length ==> ", selectedWelder.length)
      console.log("Step 6 selectedWelder ==> ", selectedWelder)
      //Step 7 -  Check wheather welder id is present if not => not linked 
      const isLinked = (linkedproject[0]?.WI_Welder_Id !== "" && linkedproject[0]?.WI_Welder_Id !== undefined) || false;
      console.log("Step 7", isLinked, linkedproject[0])
      //Step 8 - Optimized welder processing
      const uniqueWelderIds = new Set();
      const wpqRadio = linkedproject[0]?.WI_Wpq_Radio;
      console.log("Step 8 wpqRadio ", wpqRadio);
      const linkingSet = isLinked ? new Set(linkingWpqSKData) : null;
      let duplicate = {};
      for (const item of selectedWelder) {
        let WPQLinked = true;
        if (isLinked && wpqRadio !== "Linking WPQ with project not Required") {
          const recordNo = item?.PK?.split("#")?.[1];
          const linkKey = `${item.WPQ_WelderId}#${recordNo}`;
          WPQLinked = linkingSet.has(linkKey);
        }
        if (WPQLinked) {
          selectedLinkedWeldersData.push(item);
          const welderIdBefore = uniqueWelderIds.size;
          uniqueWelderIds.add(item.WPQ_WelderId);
          if (uniqueWelderIds.size > welderIdBefore) {
            uniqWelderData.push({
              WI_Welder_Id: item.WPQ_WelderId,
              WI_Welder_Name: item.WPQ_WelderName,
            });
            duplicate[item.WPQ_WelderId] = {
              WI_Welder_Id: item.WPQ_WelderId,
              WI_Welder_Name: item.WPQ_WelderName,
              Count: 1
            };
          } else {
            duplicate[item.WPQ_WelderId].Count++;
          }
        }
      }
      console.log("duplicate ", Object.values(duplicate));
      setUpdateDate(selectedLinkedWeldersData);
      //setGetWelder(uniqueData);
    }
    //Step 9 - Fetch and filter only active welders when needed
    if (uniqWelderData.length > 0) {
      try {
        const activeWelderMaster = await getActiveWelder();
        console.log("activeWelderMaster.length ==> ", activeWelderMaster.length);
        const activeWelderIds = new Set(activeWelderMaster.map(welder => welder.SK));
        console.log("activeWelderIds ==> ", activeWelderIds);
        const activeWelders = uniqWelderData.filter(welder => activeWelderIds.has(welder.WI_Welder_Id));
        console.log("actual activeWelders ==> ", activeWelders);
        setGetWelder(activeWelders);
      } catch (error) {
        console.error("Error fetching active welders:", error);
        setGetWelder([]);
      }
    }

    setLoading(false);
  };

  const fetchProject = async () => {
    let result = await getAllOnlyProject();
    setProjectDetails(result);
  };



  const updatewi = async (updatedProcesses) => {
    if (wpsData.length > 0) {
      const getwpsupdated = wpsData.filter((item) => item.SK === updatedProcesses[0].SK);
      setgetUpdatedWps(getwpsupdated);
    }
    console.log("updatedProcesses ==> ", updatedProcesses);
  };

  const fetchWpsHeaderById = async (IdprojectPK, IdprojectSK) => {
    for (let Type of GrooveType) {
      let result = await getWIbyProjectNo(Type, IdprojectPK, IdprojectSK);
      if (result.length > 0) {
        if (result != undefined) setgetheaderData(result);
      }
    }
  };

  const addwpsdata = async () => {
    const updatedProcesses = [...WiObj.Processes];
    let temp = [];
    let temppno = [];
    let temppno1 = [];
    let fnoarr = await getFcatByFno({ "WPSD_FM_FNo": UpdatedWps[0]?.WPSD_FM_FNo });
    if (fnoarr && fnoarr.length > 0) {
      temp = fnoarr.map(ele => ele.wld_category);
    }
    let pnoarr = await getPcatByPno({ "PNO": getheaderData[0]?.WPSH_BM_Type_PNo });
    if (pnoarr && pnoarr.length > 0) {
      temppno = pnoarr.map(ele => ele.wld_p_category);
    }
    let pnoarr1 = await getPcatByPno({ "PNO": getheaderData[0]?.WPSH_BM_WeldingTo_PNo });
    if (pnoarr1 && pnoarr1.length > 0) {
      temppno1 = pnoarr1.map(ele => ele.wld_p_category);
    }
    updatedProcesses[0] = {
      ...updatedProcesses[0],
      SK: UpdatedWps[0]?.SK || "",
      WPSD_WldProcessCatagoryType: UpdatedWps[0]?.WPSD_WP_WeldingProcess + "#" + UpdatedWps[0]?.SK.split("#")[1],
      WPSD_WP_FillerMetalProductForm: UpdatedWps[0]?.WPSD_WP_FillerMetalProductForm || "",
      WPSH_consumRadio: getheaderData[0]?.WPSH_consumRadio,
      WPSD_WP_ArcTransferMode: UpdatedWps[0]?.WPSD_WP_ArcTransferMode || "",
      WPSD_WP_CurrentPolarity: UpdatedWps[0]?.WPSD_WP_CurrentPolarity,
      WPSD_FM_FNo: temp,
      WPSD_WP_Multiple_SinglePassPerSide: UpdatedWps[0]?.WPSD_WP_Multiple_SinglePassPerSide || ""
    };
    if (wpsData[0]?.wld_WPSType === "Tubesheet") {
      setWiObj((prev) => ({
        ...prev,
        Processes: updatedProcesses,
        wld_WPSType: getheaderData[0]?.wld_WPSType,
        WPSH_TRQ_BaseMetalCRO_Max: getheaderData[0]?.WPSH_TRQ_BaseMetalCRO_Max,
        WPSH_BM_Type_PNo: temppno,
        WPSH_BM_WeldingTo_PNo: temppno1,
        WPSH_consumRadio: getheaderData[0]?.WPSH_consumRadio,
        WI_Welder_Id: WiObj[0]?.WI_Welder_Id,
        WI_Welder_Name: WiObj[0]?.WI_Welder_Name,
        WI_Flux: "-",
        WI_Flux_Brand: "-",
        WI_Flux_ItemCode: "-",
        WI_Flux_Batch: "-",
        WI_NOTES: getheaderData[0]?.WPSH_Notes,
        WI_Wrap_Insulation: getheaderData[0]?.WPSH_PWHeatTre_Wrap_insulation_material
      }));
    }
    else {
      setWiObj((prev) => ({
        ...prev,
        Processes: updatedProcesses,
        wld_WPSType: getheaderData[0]?.wld_WPSType,
        WPSH_TRQ_BaseMetalCRO_Max: getheaderData[0]?.WPSH_TRQ_BaseMetalCRO_Max,
        WPSH_BM_Type_PNo: temppno,
        WPSH_BM_WeldingTo_PNo: temppno1,
        WPSH_consumRadio: getheaderData[0]?.WPSH_consumRadio,
        WI_Welder_Id: getheaderData[0]?.WPQ_WelderId,
        WI_Welder_Name: getheaderData[0]?.WPQ_WelderName,
        WI_Flux: fluxDetailsInWISlipCheck ? getheaderData[0]?.WPSH_Flux_Classification : "-",
        WI_Flux_Brand: fluxDetailsInWISlipCheck ? getheaderData[0]?.WPSH_Flux_ChemicalAnalysis_TradeName : "-",
        WI_Flux_ItemCode: getheaderData[0]?.WPSH_FM_FluxItemCode,
        WI_Flux_Batch: getheaderData[0]?.WPSH_FM_FluxItemBatch,
        WI_NOTES: getheaderData[0]?.WPSH_Notes,
        WI_Wrap_Insulation: getheaderData[0]?.WPSH_PWHeatTre_Wrap_insulation_material
      }));
    }
  };

  useEffect(() => {
    fetchProject();
    fetchActiveWeldingProcess();
  }, []);



  const fetchActiveWeldingProcess = async () => {
    const result = await getActiveProcess();
    setActiveWldProcCategories(result);
  };

  const getFilteredActiveProcesses = (wpsDataArray) => {
    if (!wpsDataArray?.length || !activeWldProcCategories?.length) {
      return [];
    }

    const activeProcessCodes = new Set(activeWldProcCategories.map(proc => proc.wld_code));
    const filteredProcesses = wpsDataArray.filter(wpsItem => activeProcessCodes.has(wpsItem.WPSD_Process));

    if (filteredProcesses.length === 0) {
      showWarning("Selected WPS contains processes that are not active in Welding Process Master. Please contact administrator.");
    }

    return filteredProcesses;
  };

  useEffect(() => {
    if (WiObj.WI_Type !== "" && WiObj.WI_Process !== "") {
      addwpsdata();
    }
  }, [WiObj.WI_Process, WiObj.WI_Type]);



  const resetForm = () => {
    if (action === "Add") {
      setWiObj((prevState) => ({
        ...prevState,
        WI_Project_No: "",
        WCS_Project_No: "",
        WI_Joint_No: "",
        WI_WPS_No: "",
        WI_Process: "",
        WI_Type: "",
        WI_Overlay_Joint_BMT: "",
        WI_PipePlate: "",
        WI_Pipe_Diameter: "",
        WI_Welding_Position: "",
        WI_Welder_Id: "",
        WI_Welder_Name: "",
        WI_plant: "",
      }));
    } else if (action === "Edit") {
      setWiObj((prevState) => ({
        ...prevState,
        PK: props.rowdata.PK,
        SK: props.rowdata.SK,
        wld_status: props.rowdata.wld_status,
        wld_code: props.rowdata.wld_code,
        wld_p_category: props.rowdata.wld_p_category,
        wld_description: props.rowdata.wld_description,
        wld_createdby: props.rowdata.wld_createdby,
        wld_createdon: props.rowdata.wld_createdon,
      }));
    }
    setError((prevState) => ({
      ...prevState,
      wld_code: {},
      wld_p_category: {},
      wld_description: {},
      SK: {},
    }));
  };


  /*
  ================================================================================
                        WI SLIP GENERATION - LAST WELD DATE UPDATE
  ================================================================================
  
  CRITICAL FUNCTION: Updates last weld dates when WI slip is generated
  
  RECORDS UPDATED:
  • Detail Records: wld_createdon, WPQ_Welder_ExpiresOn, WI_WPQ_SlipGenerationStatus
  • Header Records: WQC_Last_Weld_Date array for WQC reporting
  
  UPDATE SOURCES:
  • updateDate: Selected WPQ records for Groove/Overlay (from fetchWelder)
  • tubeWeldersData: Linked WPQ SK data for Tubesheet (from fetchWelder)
  
  BUSINESS IMPACT:
  • Extends qualification validity by 178 days from current date
  • Maintains process category synchronization
  • Enables continuous production without re-qualification
  ================================================================================
  */
  const handleAdd = async () => {
    setLoading(true);
    let result;
    let actualSelectedWeldersWPQ;
    if (action === "Add") {
      // STEP 1: Get WPQ records for selected welder from updateDate state
      // updateDate populated by fetchWelder() during welder selection
      let Result1 = updatedate;  // Contains qualified WPQ records
      console.log("updatedate ==> ", updatedate);
      actualSelectedWeldersWPQ = Result1.filter(
        (item) => item.WPQ_WelderId === WiObj?.WI_Welder_Id
      );
      console.log("Filterwelder WPQ from Qualified actualSelectedWeldersWPQ ==> ", actualSelectedWeldersWPQ);
      console.log("🔄 LAST WELD DATE UPDATE: Found", actualSelectedWeldersWPQ.length, "WPQ records for welder", WiObj?.WI_Welder_Id);
      WiObj.WI_FillerMetalSize = UpdatedWps[0]?.WPSD_WP_FillerMetalSIze;
      WiObj.WI_WP_BatchNumber = UpdatedWps[0]?.WPSD_WP_BatchNumber;
      WiObj.WI_ItemDesc = UpdatedWps[0]?.welddesc;
      WiObj.WI_WCinKgElectrode = UpdatedWps[0]?.weldweight;
      WiObj.WI_FM_Brand = UpdatedWps[0]?.WPSD_FM_ChemicalAnalysis_TradeName;
      WiObj.WI_FM_AWS = UpdatedWps[0]?.WPSD_FM_Classification;

      let category = activeWldProcCategories.filter((element) => element.wld_code === WiObj.WI_Process)[0];
      WiObj.WI_ProcessCategory = UpdatedWps[0]?.WPSD_ProcessCategory + "#" + category.wld_p_category;
      WiObj.WPS_Rev_no = getheaderData[0]?.WPSH_WPS_Revi_No;
      WiObj.WI_Pre_Heat_Temp = getheaderData[0]?.WPSH_PWHeatTre_Post_Heat_temp;
      WiObj.WI_Pre_Heat_Time = getheaderData[0]?.WPSH_PWHeatTre_Post_Heat_time;
      WiObj.WI_CreatedBy = emailid;
      WiObj.WI_CreatedBy_Name = username;
      WiObj.WI_WPSHeaderSK = getheaderData[0]?.SK;
      WiObj.WI_WPSDetailSK = UpdatedWps[0]?.SK;
      WiObj.WI_JointDesign = getheaderData[0]?.wld_WPSType;

      // STEP 2: LAST WELD DATE UPDATE - Process WPQ records based on type
      let counter = 0;
      let array = [];  // Batch update promises

      if (getheaderData[0]?.wld_WPSType === "Tubesheet") {
        console.log("🔧 TUBESHEET UPDATE: Processing Tubesheet WPQ records");
        WiObj.WI_WeldConsumable = UpdatedWps[0]?.WPSD_WP_ItemCode;
        WiObj.WI_ItemDesc = UpdatedWps[0]?.welddesc;
        WiObj.WI_WCinKgElectrode = UpdatedWps[0]?.weldweight;

        // TUBESHEET: Use tubeWeldersData (linkingWpqSKData from fetchWelder)
        let wpqDataArr = [];
        for (let element of  tubeWeldersData) {
          let welder = element.split("#");  // Format: "WelderId#RecordNo"
          if (welder[0] === WiObj.WI_Welder_Id) {
            const wpqDetailRecord = await getdetailsById(welder[0], welder[1]);
            console.log("Tubesheet wpqDetailRecord ==> ", wpqDetailRecord);

            if (wpqDetailRecord?.length > 0) {
              wpqDataArr.push(wpqDetailRecord[0]);
            }
            //wpqDataArr.push({ welderId: welder[0], wpqRecordNo: welder[1] });
          }

        }
        console.log("wpqDataArr : ", wpqDataArr)

        console.log("🔧 TUBESHEET UPDATE: Found", wpqDataArr.length, "linked WPQ of the selected Welder records to update");

        // Update last weld date for each Tubesheet WPQ record
        for (let record of wpqDataArr) {
          console.log(`📝 TUBESHEET UPDATE: Updating WPQ ${record.welderId}#${record.wpqRecordNo}`);

          // let tmpUpdateResult = updatedatewpq(
          //   record.welderId,
          //   record.wpqRecordNo,
          //   WiObj.WI_Process,
          //   WiObj.WI_Type,
          //   getheaderData[0].wld_WPSType
          // );
          let updateDateResult = updateLastWeldDateFromWI(record);
          array.push(updateDateResult);
          counter++;

          // Batch processing: Execute updates in groups of 5 for performance
          if (counter === 5) {
            console.log("🚀 BATCH UPDATE: Executing batch of 5 WPQ updates");
            await Promise.all(array);
            counter = 0;
            array = [];
          }
        };
      }
      else {
        console.log("⚙️ GROOVE/OVERLAY UPDATE: Processing Groove/Overlay WPQ records");
        WiObj.WI_WeldConsumable = UpdatedWps[0]?.WPSD_WP_WeldConsumable;
        WiObj.WI_Weld_Max = UpdatedWps[0]?.WPSD_FM_TRQ_Max;

        // GROOVE/OVERLAY: Use actualSelectedWeldersWPQ (from updateDate state)
        let maxDepThickArr = [];
        console.log("⚙️ GROOVE/OVERLAY UPDATE: Processing", actualSelectedWeldersWPQ.length, "WPQ records");

        for (let item of actualSelectedWeldersWPQ) {
          let wpqPKKeys = item.PK.split("#");  // Format: "WelderId#RecordNo"
          let selectedWelderId = wpqPKKeys[0];
          let selectedWPQNo = wpqPKKeys[1];
          // Get weld deposit thickness for WI slip
          let depThickResult = await getbyprojectnoWPQ_WI(
            actualSelectedWeldersWPQ[0]?.wld_WPSType,
            selectedWelderId,
            selectedWPQNo
          );

          console.log("depThickResult ==> ", depThickResult);
          let getMaxValue = depThickResult[0]?.WPQ_FM_Weld_Deposit_Thickness;
          console.log("getMaxValue ==> ", getMaxValue);
          maxDepThickArr.push(getMaxValue);

          // CRITICAL: Update last weld date for this WPQ record
          let wpqSKKeys = item.SK.split("#");  // Format: "Process#Type"
          let itemProcess = wpqSKKeys[0];      // e.g., "GTAW"
          let itemProcessType = wpqSKKeys[1];  // e.g., "Groove"

          console.log(`📝 GROOVE/OVERLAY UPDATE: Updating WPQ ${selectedWelderId}#${selectedWPQNo} - ${itemProcess}#${itemProcessType}`);
          // // Call updatedatewpq to update last weld date (calls /updatedate endpoint)
          // // This updates: wld_createdon, WPQ_Welder_ExpiresOn, WI_WPQ_SlipGenerationStatus
          // // Also updates header WQC_Last_Weld_Date for reporting
          // let tmpUpdateResult = updatedatewpq(
          //   selectedWelderId,    // Welder ID
          //   selectedWPQNo,       // WPQ Record Number
          //   itemProcess,         // Process (e.g., "GTAW")
          //   itemProcessType,     // Process Type (e.g., "Groove")
          //   item.wld_WPSType     // WPS Type for backend logic
          // );
          // array.push(tmpUpdateResult);
          // counter++;

          // // Batch processing: Execute updates in groups of 5 for performance
          // if (counter === 5) {
          //   console.log("🚀 BATCH UPDATE: Executing batch of 5 WPQ updates");
          //   await Promise.all(array);
          //   counter = 0;
          //   array = [];
          // }
        }

        console.log("maxDepThickArr ==> ", maxDepThickArr);
        // Set weld deposit thickness for WI slip
        WiObj.WI_WeldDepThick = maxDepThickArr.includes("No Limit") ? "No Limit" : Math.max(...maxDepThickArr.filter(n => typeof n === "number")).toString();

        //Call update last weld date api for Groove/Overlay at once as the actualSelectedWeldersWPQ are the WPQs of the selected welder and this are fetched by comparision that means already Process category, process type is compared. We have to update the last weld date of the all wpqs (Groove/Overlay/Tubesheet).
        let updateLastWeldDateResult = updateLastWeldDateFromWI(actualSelectedWeldersWPQ[0]);
        array.push(updateLastWeldDateResult);
      }
      console.log("WiObj.WI_WeldDepThick ==> ", WiObj.WI_WeldDepThick);
      // STEP 3: Execute remaining batch updates
      if (array.length > 0) {
        console.log("🚀 FINAL BATCH LAST WELD DATE UPDATE: Executing remaining", array.length, "WPQ updates");
        await Promise.all(array);
        console.log("✅ LAST WELD DATE UPDATE: All WPQ records updated successfully");
      }
      result = await addwelder(WiObj);
      if (result) {
        setshowConfirmModel(false);
        const generatedSlipNumber = result.WI_Slip;
        WiObj.WI_Slip = generatedSlipNumber;
        
        showSuccess(`Selected WI Slip has been Added successfully`);
        props.setReload(!props.reload);
        handlePdfGeneration(WiObj, generatedSlipNumber);
        
        if (result.message === "Duplicate") {
          showWarning(result.message);
        }
      }
      else {
        setLoading(false);
        showError(`Unable to add WI Slip.`);
      }
    }
  };

  const handlePdfGeneration = async (wiObj, slipNumber) => {
    setLoading(true);
    try {
      console.log("Starting PDF generation with wiObj:", wiObj);
      console.log("Using slip number:", slipNumber);
      
      const resultWI = await getallWelderid(slipNumber);
      console.log("WI Result:", resultWI);
      
      if (!resultWI || resultWI.length === 0) {
        throw new Error("No WI data found for slip number: " + slipno);
      }
      
      let wiProcess = resultWI[0].WI_Process;
      console.log("WI Process:", wiProcess);
      
      const resultWPS = await getWPSDetailsById(wiObj.WI_Project_No, resultWI[0]?.WI_WPS_No);
      console.log("WPS Result:", resultWPS);
      let wpsDetailData = resultWPS.filter((item) => item.WPSD_Process === wiProcess);
      console.log("Filtered WPS Data:", wpsDetailData);
      
      let procCat = activeWldProcCategories.filter((element) => element.wld_code === wpsDetailData[0]?.WPSD_Process)[0];
      if (procCat) {
        wpsDetailData[0]["WPSDProcessCat"] = procCat?.wld_p_category;
      }
      if (wpsDetailData[0].wld_WPSType === "Tubesheet") {
        wpsDetailData[0]["WPSDType"] = wpsDetailData[0]?.WPSD_WP_Type;
      }
      else {
        wpsDetailData[0]["WPSDType"] = wpsDetailData[0]?.WPSD_Type;
      }
      
      const combinedData = [resultWI[0], ...wpsDetailData];
      console.log("Combined Data for PDF:", combinedData);
      console.log("WPS Data for PDF:", wpsDetailData[0]);
      
      setPdfData(combinedData);
      setPdfWpsData(wpsDetailData[0]);
      setLoading(false);
      setShowPdfViewer(true);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setLoading(false);
      showError("Failed to generate PDF: " + error.message);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (error.WI_Overlay_Joint_BMT.status !== true) {
      setshowConfirmModel(true);
    }
  };

  const handleProjectChange = async (e) => {
    let projectNo = e.value;
    setWiObj((prev) => ({
      ...prev,
      WI_Project_No: projectNo,
      WCS_Project_No: projectNo,
      WI_Joint_No: "",
      WI_WPS_No: "",
      WI_Process: "",
      WI_Type: "",
      WI_Overlay_Joint_BMT: "",
      WI_PipePlate: "",
      WI_Pipe_Diameter: "",
      WI_Welding_Position: "",
      WI_Welder_Id: "",
      WI_Welder_Name: ""
    }));
    setGetWelder([]);
    setWpsData([]);
    try {
      let findprojdetails = projectDetails.filter(item => item.wld_ProjectNumber === projectNo && item.wld_inspectionagency === "NPCIL");
      if (findprojdetails.length > 0) {
        setNpcilflag(true);
        let validateProject = await getwpqlink(true, projectNo);
        setNpcilProjDet(validateProject);
        if (validateProject.length === 0) {
          showWarning("WPQ's is not linked in the session 'NPCIL Project to WPQ Linking'");
          setWiObj((prev) => ({
            ...prev,
            WI_Project_No: ""
          }));
          setJointNumbers([]);
          setWpsNumbers([]);
          return;
        }
      }
      else {
        setNpcilflag(false);
        setNpcilProjDet([]);
      }
      const result = await getWCSDataHeader({ WCS_Project_No: projectNo });
      setWcsProjectData(result);
      if (result && result.length > 1) {
        let jointArr = result[1];
        const selectedJointNos = jointArr.reduce((acc, project) => {
          if (project.WCS_Joint_No) {
            acc.push({ WI_Joint_No: project.WCS_Joint_No });
          }
          return acc;
        }, []);
        if (selectedJointNos.length > 0) {
          setJointNumbers(selectedJointNos);
        }
      }
      if (!result || result.length === 0) {
        showWarning(`Project No: ${projectNo} is not approved!`);
        setWiObj((prev) => ({
          ...prev,
          WI_Project_No: "",
          WCS_Project_No: "",
          WI_Joint_No: "",
          WI_WPS_No: "",
          WI_Process: "",
          WI_Type: ""
        }));
        setJointNumbers([]);
      }
      setWpsNumbers([]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleJointChange = async (e) => {
    const value = e.value;
    let checkJoint = false;
    checkJoint = npcilProjDet.some(ele => ele.WI_Joint_No.split(",").includes(value));

    if (npcilProjDet.length === 0 && !npcilflag) {
      checkJoint = true;
    }
    console.log("Npcil project ::", npcilflag);
    if (checkJoint) {
      setWiObj((prev) => ({
        ...prev,
        WI_Joint_No: value,
        WI_WPS_No: "",
        WI_Process: "",
        WI_Type: "",
        WI_Welding_Position: "",
        WI_Overlay_Joint_BMT: "",
        WI_PipePlate: "",
        WI_Pipe_Diameter: "",
        WI_Plate_No: "",
        WI_Welder_Id: "",
        WI_Welder_Name: ""
      }));

      if (value !== "" && wcsProjectData && wcsProjectData.length > 1) {
        let jointArr = wcsProjectData[1].filter(item => item.WCS_Joint_No === value);
        if (jointArr.length > 0) {
          const wcsWPSNos = [];
          for (let i = 1; i <= 4; i++) {
            const wcsWPSNoKey = `WCS_WPS_No${i}`;
            const wpsNo = jointArr[0][wcsWPSNoKey];
            if (wpsNo) {
              wcsWPSNos.push({
                "WI_WPS_No": wpsNo
              });
            }
          }
          setWpsNumbers(wcsWPSNos);
        }
      }
    }
    else {
      showWarning("WPQ's are not linked for selected Project Number and Joint Number");
      setWiObj((prev) => ({
        ...prev,
        WI_Project_No: "",
        WI_plant: "",
        WI_Joint_No: "",
        WCS_Project_No: "",
        WI_WPS_No: "",
        WI_Process: "",
        WI_Type: "",
        WI_Overlay_Joint_BMT: "",
        WI_PipePlate: "",
        WI_Welding_Position: "",
        WI_Welder_Id: "",
        WI_Welder_Name: ""
      }));
      setJointNumbers([]);
      setWpsNumbers([]);
      return;
    }
    setGetWelder([]);
    setWpsData([]);
  }

  const handleWpsChange = async (e) => {
    const value = e.value;
    let filteredProcess = [];
    setProcessTypes([]);
    setWiObj((prev) => ({
      ...prev,
      WI_WPS_No: value,
      WI_Process: "",
      WI_Type: "",
      WI_Overlay_Joint_BMT: "",
      WI_PipePlate: "",
      WI_Pipe_Diameter: "",
      WI_Plate_No: "",
      WI_Welding_Position: "",
      WI_Welder_Id: "",
      WI_Welder_Name: ""
    }));
    if (WiObj.WI_Project_No && value) {
      const wpsResult = await getdetailsByIdWI(WiObj.WI_Project_No, value);
      if (wpsResult?.length > 0) {
        setWpsData(wpsResult);
        // Filter processes using the fresh data
        filteredProcess = getFilteredActiveProcesses(wpsResult);
      }
      await fetchWpsHeaderById(WiObj.WI_Project_No, value);
    }
    setFilteredProcesses(filteredProcess);
    setGetWelder([]);
  }

  const handleProcessChange = async (e) => {
    const value = e.value;
    let slipCheck = false;
    let pcat = filteredProcesses.filter((b) => {
      return b["WPSD_Process"] === value;
    });

    const currentProcessTypes = filteredProcesses.filter((item) => item.WPSD_Process === value);
    setProcessTypes(currentProcessTypes);

    if (wpsData[0]?.wld_WPSType !== "Tubesheet") {
      slipCheck = pcat[0]?.WPSD_Flux_Details_WI_CHECK;
      setFluxDetailsInWISlipCheck(slipCheck);
    }

    setWiObj((prev) => {
      if (pcat[0].wld_WPSType === "Tubesheet" || pcat.length === 1) {
        const updatedProcesses = [
          {
            ...prev.Processes[0],
            SK: pcat[0].SK,
            type: pcat[0].wld_WPSType
          },
          ...prev.Processes.slice(1),
        ];
        updatewi(updatedProcesses);
        if (pcat.length === 1 && pcat[0].wld_WPSType === "Tubesheet") {
          return {
            ...prev,
            WI_Process: pcat[0].WPSD_Process,
            WI_Type: pcat[0].WPSD_WP_Type,
            Processes: updatedProcesses,
            WI_Welding_Position: "",
            WI_Overlay_Joint_BMT: "",
            WI_PipePlate: "",
            WI_Pipe_Diameter: "",
            WI_Plate_No: "",
            WI_Welder_Id: "",
            WI_Welder_Name: ""
          };
        }
        else if (pcat.length === 1 && pcat[0].wld_WPSType !== "Tubesheet") {
          return {
            ...prev,
            WI_Process: pcat[0].WPSD_Process,
            WI_Type: pcat[0].WPSD_Type,
            Processes: updatedProcesses,
            WI_Welding_Position: "",
            WI_Welder_Id: "",
            WI_Welder_Name: ""
          };
        }
      }
      else if (pcat.length > 1) {
        const updatedProcesses = [{
          ...prev.Processes[0],
          SK: "",
        },
        ...prev.Processes.slice(1),
        ];
        updatewi(updatedProcesses);
        return {
          ...prev,
          WI_Process: value,
          WI_Type: "",
          Processes: updatedProcesses,
          WI_Welding_Position: "",
          WI_PipePlate: "",
          WI_Pipe_Diameter: "",
          WI_Plate_No: "",
          WI_Welder_Id: "",
          WI_Welder_Name: ""
        };
      }
    });
  }

  const validateFieldsForWelder = async () => {
    let emptyFields = [];
    let errorMessage = "";
    let isError = false;
    if (WiObj.WI_Project_No === "") {
      isError = true;
      emptyFields.push("Project No");
    }
    if (WiObj.WI_Process === "") {
      isError = true;
      emptyFields.push("Process");
    }
    if (WiObj.WI_Joint_No === "") {
      isError = true;
      emptyFields.push("Joint No");
    }
    if (WiObj.WI_WPS_No === "") {
      isError = true;
      emptyFields.push("WPS No");
    }
    if (WiObj.WI_Type === "") {
      isError = true;
      emptyFields.push("WPS No");
    }
    if (wpsData[0]?.wld_WPSType === "Overlay" && WiObj.WI_Overlay_Joint_BMT === "") {
      isError = true;
      emptyFields.push("Overlay Joint Base Metal Thickness");
    }
    if (WiObj.WI_PipePlate === "") {
      isError = true;
      emptyFields.push("Pipe or Plate");
    }
    if (WiObj.WI_PipePlate === "Pipe" && WiObj.WI_Pipe_Diameter === "") {
      isError = true;
      emptyFields.push("Pipe Diameter");
    }
    if (isError) {
      errorMessage = "Fields Required: " + emptyFields.join(", ");
    }
    setAlertMessage(errorMessage);
    setError((prevState) => ({
      ...prevState,
      ...error,
    }));
    return isError;
  }


  const handleWeldingPositionChange = async (e) => {
    let value = e.value;
    setLoading(true);
    if (!(await validateFieldsForWelder())) {

      setWiObj((prev) => ({
        ...prev,
        WI_Welding_Position: value,
        WI_Welder_Id: "",
        WI_Welder_Name: ""
      }));

      setGetWelder([]);
      let tempWiObj = { ...WiObj };
      tempWiObj["WI_Welding_Position"] = value;
      await fetchWelder(tempWiObj);
      if (wpsData[0]?.wld_WPSType === "Tubesheet") {
        addwpsdata();
      }
    }
    setLoading(false);
  }

  const handleWelderCodeChange = async (e) => {
    if (!e || !e.value) {
      setWiObj((prev) => ({
        ...prev,
        WI_Welder_Id: "",
        WI_Welder_Name: ""
      }));
      return;
    }

    let value = e.value;
    let welderRecord = getWelder.filter((item) => item.WI_Welder_Id === value);

    if (welderRecord.length > 0) {
      setError((prevState) => ({
        ...prevState,
        WI_Welder_Id: {}
      }));

      let welderName = welderRecord[0]?.WI_Welder_Name;
      if (!welderName) {
        let welderMasterData = await getWelderById({ welderId: value });
        welderName = welderMasterData[0]?.wld_name || "";
      }

      setWiObj((prev) => ({
        ...prev,
        WI_Welder_Id: value,
        WI_Welder_Name: welderName
      }));
    } else {
      // Invalid welder code - clear fields and show error
      setWiObj((prev) => ({
        ...prev,
        WI_Welder_Id: "",
        WI_Welder_Name: ""
      }));
      setError((prevState) => ({
        ...prevState,
        WI_Welder_Id: {
          status: true,
          message: "Entered Welder Code is not valid welder code"
        }
      }));
    }
  };

  const handleWelderValidationError = (message) => {
    setError((prevState) => ({
      ...prevState,
      WI_Welder_Id: message ? { status: true, message } : {}
    }));
  };

  return (
    <>
      {loading && ReactDOM.createPortal(
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>,
        document.body
      )}
      
      <Modal
        {...props}
        size="lg"
        centered
        className={
          showForm || showSuccessModel || showConfirmModel
            ? "opacity-0 userMasterModal"
            : "opacity-100 userMasterModal"
        }
        backdrop="static"
      >
        <SuccessAlertModal
          clickFunction={modalOnClick}
          msg={`Great! You've ${action}ed WI Slip successfully.`}
          show={showSuccessModel}
          handleClose={handleCloseSuccess}
        />
        <ConfirmAlertModal
          action={action}
          handleYes={handleAdd}
          formName={formName}
          show={showConfirmModel}
          handleClose={handleCloseConf}
        />
        <Modal.Header closeButton>
          <Modal.Title>{action} New </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} autoComplete="off">
          <Modal.Body>
            <div className="px-2 pb-2 font" style={{ position: "relative", top: "12px" }}>
              <Row className="gx-3" style={{ marginBottom: "2rem" }}>
                <Col>
                  <Form.Group>
                    <div className="T-input w-100">
                      <input
                        type="text"
                        id="WI_Slip"
                        name="WI_Slip"
                        placeholder=" "
                        readOnly
                        maxLength={10}
                        className={error.wld_code.status ? "err-input" : ""}
                        value={action === "Add" ? "Will be generated after clicking on Add" : WiObj.WI_Slip}
                      />
                      <Form.Label htmlFor="wld_code">Slip Number</Form.Label>
                    </div>
                    <div className="required-text">
                      {error.wld_code.status && (
                        <Form.Text className="text-danger">
                          {error.wld_code.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="promd4-input-select">
                    <div className="search-select">
                      <SearchSelect
                        id="WI_Project_No"
                        name="WI_Project_No"
                        stylegreen={error.wld_code.status ? errorstyle : normalstyle}
                        data={projectDetails}
                        valueField={"wld_ProjectNumber"}
                        value={WiObj.WI_Project_No}
                        labelField={"wld_ProjectNumber"}
                        placeholder=""
                        onChange={handleProjectChange}
                      ></SearchSelect>
                    </div>
                    <Form.Label>Project Number</Form.Label>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <General_Single_Input_select
                      name="WI_plant"
                      type="text"
                      maxLength={20}
                      status={error.WI_plant.status}
                      value={WiObj?.WI_plant}
                      handleSelectChange={handleChange}
                      label="Plant"
                      array={Plant_Location_WI}
                    ></General_Single_Input_select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="promd4-input-select">
                    <div className="search-select">
                      <SearchSelect
                        id="WI_Joint_No"
                        name="WI_Joint_No"
                        stylegreen={error.wld_code.status ? errorstyle : normalstyle}
                        data={jointNumbers}
                        valueField={"WI_Joint_No"}
                        value={WiObj.WI_Joint_No}
                        labelField={"WI_Joint_No"}
                        placeholder=""
                        onChange={handleJointChange}
                      ></SearchSelect>
                    </div>
                    <Form.Label htmlFor="WI_Joint_No">Joint Number</Form.Label>
                  </Form.Group>
                </Col>
                <Col md={4} >
                  <Form.Group className="promd4-input-select">
                    <div className="search-select">
                      <SearchSelect
                        id="WI_WPS_No"
                        name="WI_WPS_No"
                        stylegreen={error.wld_code.status ? errorstyle : normalstyle}
                        data={wpsNumbers}
                        valueField={"WI_WPS_No"}
                        value={WiObj.WI_WPS_No}
                        labelField={"WI_WPS_No"}
                        placeholder=""
                        onChange={handleWpsChange}
                      ></SearchSelect>
                    </div>
                    <Form.Label>WPS Number</Form.Label>
                  </Form.Group>
                </Col>
                <Col md={4}>
                </Col>
                <Col md={4}>
                  <Form.Group className="promd4-input-select">
                    <div className="search-select">
                      <SearchSelect
                        id="WI_Process"
                        name="WI_Process"
                        stylegreen={error.wld_code.status ? errorstyle : normalstyle}
                        data={filteredProcesses}
                        valueField={"WPSD_Process"}
                        value={WiObj.WI_Process}
                        labelField={"WPSD_Process"}
                        placeholder=""
                        onChange={handleProcessChange}
                      />
                    </div>
                    <Form.Label>Process</Form.Label>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="T-input w-100">
                    <div className="d-flex align-items-center">
                      {processTypes.length > 1 ? (
                        <select
                          id="WI_Type"
                          name="WI_Type"
                          value={WiObj.WI_Type}
                          onChange={(e) => {
                            let pcat = filteredProcesses.filter((b) => {
                              return b.WPSD_Type === e.target.value;
                            });
                            setWiObj((prev) => {
                              const updatedProcesses = [
                                {
                                  ...prev.Processes[0],
                                  SK: pcat[0].SK,
                                },
                                ...prev.Processes.slice(1),
                              ];
                              updatewi(updatedProcesses);
                              return {
                                ...prev,
                                WI_Process: WiObj.WI_Process,
                                WI_Type: e.target.value,
                                Processes: updatedProcesses,
                                WI_Welder_Id: "",
                                WI_Welder_Name: ""
                              };
                            })
                          }}
                        >
                          <option value="">Select Type</option>
                          {processTypes.map((typeItem) => (
                            <option key={typeItem.SK} value={typeItem.WPSD_Type}>
                              {typeItem.WPSD_Type}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          id="WI_Type"
                          name="WI_Type"
                          placeholder=" "
                          readOnly
                          maxLength={10}
                          className={error.wld_code.status ? "err-input" : ""}
                          value={processTypes[0]?.WPSD_Type || WiObj.WI_Type}
                        />
                      )}
                      <Form.Label htmlFor="WI_Type">Type</Form.Label>
                    </div>
                    <div className="required-text">
                      {error.wld_code.status && (
                        <Form.Text className="text-danger">
                          {error.wld_code.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={4} >
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <div className="T-input w-100">
                      <input
                        type="text"
                        id="WI_Overlay_Joint_BMT"
                        name="WI_Overlay_Joint_BMT"
                        placeholder=" "
                        maxLength={10}
                        className={error.wld_code.status ? "err-input" : ""}
                        value={WiObj.WI_Overlay_Joint_BMT}
                        onChange={(e) => {
                          handleChange(e);
                          setGetWelder([]);
                          setWiObj((prev) => ({
                            ...prev,
                            WI_Pipe_Diameter: "",
                            WI_Plate_No: WiObj.WI_PipePlate === "Plate" ? "73" : "",
                            WI_Welding_Position: "",
                            WI_Welder_Id: "",
                            WI_Welder_Name: "",
                          }));
                        }}
                        onInput={(e) => {
                          let error = twodecimal(e.target.value);
                          setError((prevState) => ({
                            ...prevState,
                            WI_Overlay_Joint_BMT: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="WI_Overlay_Joint_BMT">
                        Overlay Joint Base Metal Thickness
                      </Form.Label>
                    </div>
                    <div className="required-text">
                      {error.WI_Overlay_Joint_BMT.status && (
                        <Form.Text className="text-danger">
                          {error.WI_Overlay_Joint_BMT.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="gx-3" style={{
                marginBottom: "2rem",
                position: "relative",
                bottom: "18px"
              }}
              >
                <Col md={2}>
                  <Form.Group>
                    <div>
                      <input
                        type="radio"
                        name="WI_PipePlate"
                        value="Pipe"
                        checked={WiObj.WI_PipePlate === "Pipe"}
                        onChange={(e) => {
                          handleChange(e);
                          setGetWelder([]);
                          setWiObj((prev) => ({
                            ...prev,
                            WI_Pipe_Diameter: "",
                            WI_Plate_No: "",
                            WI_Welding_Position: "",
                            WI_Welder_Id: "",
                            WI_Welder_Name: "",
                          }));
                        }}
                      />
                      <span className="ms-2">Pipe</span>
                    </div>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <div>
                      <input
                        type="radio"
                        name="WI_PipePlate"
                        value="Plate"
                        checked={WiObj.WI_PipePlate === "Plate"}
                        onChange={(e) => {
                          handleChange(e);
                          setGetWelder([]);
                          setWiObj((prev) => ({
                            ...prev,
                            WI_Plate_No: "73",
                            WI_Pipe_Diameter: "",
                            WI_Welding_Position: "",
                            WI_Welder_Id: "",
                            WI_Welder_Name: "",
                          }));
                        }}
                      />
                      <span className="ms-2">Plate</span>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="gx-3" style={{ position: "relative", bottom: "25px" }}>
                <Col md={4}>
                  <Form.Group>
                    <div className="T-input w-100">
                      <input
                        type="text"
                        id="WI_Pipe_Diameter"
                        name="WI_Pipe_Diameter"
                        placeholder=" "
                        disabled={WiObj.WI_PipePlate === "Plate"}
                        maxLength={10}
                        className={error.wld_code.status ? "err-input" : ""}
                        value={WiObj.WI_Pipe_Diameter}
                        onChange={(e) => {
                          handleChange(e);
                          setGetWelder([]);
                          setWiObj((prev) => ({
                            ...prev,
                            WI_Pipe_Diameter: e.target.value,
                            WI_Welding_Position: "",
                            WI_Welder_Id: "",
                            WI_Welder_Name: "",
                          }));
                        }}
                        onInput={(e) => {
                          let error = isAlphanumericWithSpaceDotDash(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            wld_code: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="wld_code">Pipe Diameter</Form.Label>
                    </div>
                    <div className="required-text">
                      {error.wld_code.status && (
                        <Form.Text className="text-danger">
                          {error.wld_code.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="promd4-input-select">
                    <div className="search-select">
                      <SearchSelect
                        id="WI_Welding_Position"
                        name="WI_Welding_Position"
                        stylegreen={error.wld_code.status ? errorstyle : normalstyle}
                        data={weldingPositions}
                        valueField={"WI_Welding_Position"}
                        value={WiObj.WI_Welding_Position}
                        labelField={"WI_Welding_Position"}
                        placeholder=""
                        onChange={handleWeldingPositionChange}
                      ></SearchSelect>
                    </div>
                    <Form.Label>Welding Position</Form.Label>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="promd4-input-select">
                    <div className="search-select">
                      <SearchSelectWithInput
                        id="WI_Welder_Id"
                        name="WI_Welder_Id"
                        stylegreen={error.WI_Welder_Id.status ? errorstyle : normalstyle}
                        data={getWelder}
                        valueField={"WI_Welder_Id"}
                        value={WiObj.WI_Welder_Id}
                        labelField={"WI_Welder_Id"}
                        placeholder=""
                        mandatory={true}
                        onChange={handleWelderCodeChange}
                        onValidationError={handleWelderValidationError}
                        validationMessage="Entered Welder Code is not valid welder code"
                      ></SearchSelectWithInput>
                    </div>
                    <Form.Label htmlFor="WI_Welder_Id">Welder Code</Form.Label>
                  </Form.Group>
                  <div className="required-text-select">
                    {error.WI_Welder_Id.status && (
                      <Form.Text className="text-danger">
                        {error.WI_Welder_Id.message}
                      </Form.Text>
                    )}
                  </div>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <div className="T-input w-100">
                      <input
                        type="text"
                        id="WI_Welder_Name"
                        name="WI_Welder_Name"
                        placeholder=" "
                        readOnly
                        maxLength={10}
                        className={error.wld_code.status ? "err-input" : ""}
                        value={WiObj.WI_Welder_Name}
                      />
                      <Form.Label htmlFor="wld_code">Welder Name</Form.Label>
                    </div>
                    <div className="required-text">
                      {error.wld_code.status && (
                        <Form.Text className="text-danger">
                          {error.wld_code.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </div>
            {alertMessage && (
              <Alert className="alertBox" variant="warning">
                {alertMessage}
              </Alert>
            )}
          </Modal.Body>
          <Row>
            <ButtonModal action={action} resetForm={resetForm}></ButtonModal>
          </Row>
          <Row>
            <br></br>
          </Row>
        </Form>
      </Modal>

      {showPdfViewer && (
        <WiPdfViewer
          show={showPdfViewer}
          rowdata={pdfData}
          wpsde={pdfWpsData}
          onHide={() => {
            setShowPdfViewer(false);
            props.setReload(!props.reload);
            props.onHide();
          }}
        />
      )}
    </>
  );
};

export default AddEditWIPage;